import React, {useEffect, useState} from 'react';
import {useLocation, useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {Button, Dialog, ErrorBlock, NavBar, Skeleton, Toast} from 'antd-mobile';

import {useQuery} from "@apollo/client";
import {
    GET_DSP_PACKAGE_DETAIL,
    GetDspPackageDetailByTrackingNumberResponse,
    GetDspPackageDetailByTrackingNumberVariables
} from "../../graphql/queries/getPackageDetail";
import {
    DeliveryFailedEventInput,
    handleDeliveryFailedEvent,
    handleDspFailed
} from "../../../delivery/graphql/mutations/deliveredFailed";
import {DspPath} from "../../../../enums/DeliveryNavigatePathEnum";
import {InfoRow} from "../../../../components/InfoRow";
import {formatTimestampToSecondsTime} from "../../../../utils/dateTimeUtils";
import photoService, {PhotoType, PositionType } from 'pages/delivery/services/PhotoService';
import { useLocationStore } from 'store/locationStore';
import { ExceptionDictionaryType } from 'pages/delivery/graphql/queries/getExceptionDictionaryForDelivery';
import { NativePermissions } from 'pages/delivery/components/PhotoNativeBridge';
import {getBottomNavBarContainerStyle, getNavBarContainerStyle} from 'utils/deviceUtils';
import { t } from 'i18n';
import {useTranslation} from "react-i18next";
import {uploadPhotosWithTimeout} from "../../../../services/DeliveryDataService";
import {formatStopNumber} from "../../../../utils/stopNumberUtils";
import {getExceptionTypeText, getLocalizedText} from "../../../../utils/i18nUtils";
import PhotoUploadCard from "../../../delivery/components/PhotoUploadCard";
import PhotoViewer from "../../../delivery/components/PhotoViewer";
import {DspValidationType} from "../../../../enums/DspValidationType";
import OrderNoteItem from '../../../../components/OrderNoteItem/OrderNoteItem';
import {maskPhoneNumber} from "../../../../utils/phoneUtils";

interface DspMarkExceptionProps {
}

const DspMarkExceptionPage: React.FC<DspMarkExceptionProps> = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [photos, setPhotos] = useState<Record<PositionType, PhotoType | undefined>>({} as Record<PositionType, PhotoType | undefined>);
    const [viewingPhoto, setViewingPhoto] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const {position} = useLocationStore();
    const {trackingNumber} = useParams<{ trackingNumber: string }>();
    const { i18n } = useTranslation();
    // 获取异常原因：URL 查询参数获取
    const getExceptionReason = (): ExceptionDictionaryType | null => {
        const exceptionReasonParam = searchParams.get('exceptionReason');
        if (exceptionReasonParam) {
            return JSON.parse(decodeURIComponent(exceptionReasonParam));
        }
        return null;
    };

    // 获取异常二级原因：URL 查询参数获取
    const getExceptionSubReasons = (): Array<{ code: string; textZh: string; textEn: string; textEs?: string }> => {
        const param = searchParams.get('exceptionSubReasons');
        if (param) {
            try {
                const parsed = JSON.parse(decodeURIComponent(param));
                return Array.isArray(parsed) ? parsed : [];
            } catch {
                return [];
            }
        }
        return [];
    };

    const exceptionReason = getExceptionReason();
    const exceptionSubReasons = getExceptionSubReasons();
    const urlTitle = searchParams.get('title');
    const urlValidationType = searchParams.get('validationType') as DspValidationType;

    // 查询DSP包裹详情
    const {
        data: packageData,
        loading,
        error
    } = useQuery<GetDspPackageDetailByTrackingNumberResponse, GetDspPackageDetailByTrackingNumberVariables>(
        GET_DSP_PACKAGE_DETAIL,
        {
            variables: {
                trackingNumber: trackingNumber || ""
            },
            skip: !trackingNumber,
            fetchPolicy: 'cache-and-network'
        }
    );
    const packageDetail = packageData?.getDspPackageDetailByTrackingNumber;



    useEffect(() => {
        if (!packageDetail) return;
        const podImages = packageDetail?.exceptionImageS3UrlsJson || [];
        const podImageKeys = packageDetail?.exceptionImageS3KeysJson || [];
        if (podImages.length > 0 && podImageKeys.length > 0){
            setPhotos({
                label: {
                    url: podImages[0],
                    objectKey: podImageKeys[0]
                },
                location: {
                    url: podImages[1],
                    objectKey: podImageKeys[1]
                },
                houseNumber: undefined
            });
        }
    }, [packageDetail]);

    useEffect(() => {
        const requestPermissions = async () => {
            try {
                await NativePermissions.request('camera');
                await NativePermissions.request('storage');
            } catch (error) {
                console.error('Failed to request permissions:', error);
            }
        };

        requestPermissions();
    }, []);

    if (loading) {
        return (
            <div className="flex flex-col h-screen bg-white">
                <div {...getNavBarContainerStyle()}>
                    <NavBar onBack={() => navigate(-1)}>{trackingNumber}</NavBar>
                </div>
                <Skeleton.Title animated/>
                <Skeleton.Paragraph lineCount={5} animated/>
                <Skeleton.Title animated/>
                <Skeleton.Paragraph lineCount={3} animated/>
                <Skeleton.Title animated/>
                <Skeleton.Paragraph lineCount={1} animated/>
                <Skeleton style={{width: '100%', height: '30%'}} animated/>
            </div>
        );
    }

    if (!trackingNumber || error || !packageDetail) {
        return (
            <div className="p-4">
                <div {...getNavBarContainerStyle()}>
                    <NavBar onBack={() => navigate(-1)}>{trackingNumber}</NavBar>
                </div>
                <ErrorBlock
                    status="empty"
                    title={t('pages.dsp.packageDetail.packageNotFound', '包裹不存在')}
                    description={t('pages.dsp.packageDetail.packageNotFoundDescription', '未找到该运单号对应的包裹信息')}
                />
            </div>
        );
    }

    // 检查是否有异常原因信息
    if (!exceptionReason) {
        return (
            <div className="p-4">
                <div {...getNavBarContainerStyle()}>
                    <NavBar onBack={() => navigate(-1)}>{trackingNumber}</NavBar>
                </div>
                <ErrorBlock
                    status="empty"
                    title={t('pages.delivery.exception.noExceptionReason', '缺少异常信息')}
                    description={t('pages.delivery.exception.noExceptionReasonDescription', '未找到异常原因信息，请重新选择异常类型')}
                />
            </div>
        );
    }

    const buildNavigationParams = () => {
        const queryParams = new URLSearchParams();
        queryParams.set('successPath', DspPath.DELIVERY_DSP_MARK_EXCEPTION);
        if (urlTitle) {
            queryParams.set('title', urlTitle);
        }
        if (urlValidationType) {
            queryParams.set('validationType', urlValidationType);
        }
        return queryParams;
    };

    /*照片相关操作 start======*/

    // 拍照或从相册选择
    const takePhoto = async (type: PositionType, file: File) => {
        try {
            setIsLoading(true);
            await photoService.handlePhotoUnUpload(type, file, (updateFn) => {
                setPhotos((prev) => updateFn(prev));
            });
        } catch (error) {
            console.error('Failed to take photo:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // 删除照片
    const deletePhoto = (type: PositionType) => {
        Dialog.confirm({
            content: t('pages.delivery.exception.deletePhotoTitle'),
            confirmText: t('pages.delivery.exception.deletePhotoDelete'),
            cancelText: t('pages.delivery.exception.deletePhotoCancel'),
            onConfirm: () => {
                photoService.handleDeletePhoto(type, (updateFn) => {
                    setPhotos((prev) => updateFn(prev));
                });
            },
        });
    };

    // 查看大图
    const handleViewPhoto = (url: string) => setViewingPhoto(url);

    /*照片相关操作 end======*/

    // 标记异常
    const handleCompleteException = async () => {
        setIsLoading(true);

        try {
            const hasLabel = photos.label?.objectKey;
            const hasLocation = photos.location?.objectKey;
            if (exceptionReason.isUploadsImages && (!hasLabel || !hasLocation)) {
                Toast.show({content: 'Please add a photo of the cause of the anomaly', position: 'center'});
                setIsLoading(false);
                return;
            }

            // 获取需要上传的照片
            const photosToUpload = Object.values(photos).filter((item): item is PhotoType => item?.objectKey !== undefined && item?.url !== undefined);
            // 执行照片上传
            const photoKeys = await uploadPhotosWithTimeout(photosToUpload, trackingNumber);


            const failedInput : DeliveryFailedEventInput = {
                trackingNumber: trackingNumber,
                location: {
                    latitude: position?.latitude || 0.0,
                    longitude: position?.longitude || 0.0
                },
                exceptionReasonCode: exceptionReason.exceptionReasonCode,
                id: packageDetail?.deliveryTaskOrder?.id || 0,
                exceptionImageS3KeysJson: photoKeys,
                exceptionSubReasons: exceptionSubReasons.length > 0 ? exceptionSubReasons.map(s => s.code) : undefined
            };
            await handleDspFailed(failedInput);

            Toast.show({ content: t('pages.delivery.exception.markAsFailed'), position: 'center' });
            if (urlTitle || urlValidationType) {
                const params = buildNavigationParams();
                navigate(`${DspPath.DELIVERY_DSP_PACKAGE_SCAN}?${params.toString()}`);
            } else {
                navigate(DspPath.DELIVERY_DSP_HOME);
            }

        } catch (error: any) {
            console.error('Mark failed:', error);
            Toast.show({content: t('pages.delivery.orderDetail.markFailed'), position: 'center'});

        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex flex-col h-screen">
            <div {...getNavBarContainerStyle()}>
                <NavBar onBack={() => {
                    if (urlTitle || urlValidationType) {
                        const params = buildNavigationParams();
                        navigate(`${DspPath.DELIVERY_DSP_PACKAGE_SCAN}?${params.toString()}`);
                    } else {
                        navigate(DspPath.DELIVERY_DSP_HOME);
                    }
                }}>
                    {trackingNumber}
                </NavBar>
            </div>

            <div className="flex-1 overflow-y-auto">
                <div className="mb-1 bg-white p-4">
                    <h2 className="text-base font-medium">{t('pages.dsp.packageDetail.orderInfo')}</h2>
                    <div className="mb-1">
                        <InfoRow
                            label={t('pages.dsp.packageDetail.trackingNo')}
                            value={packageDetail.trackingNumber}
                        />
                        <InfoRow
                            label={t('pages.dsp.packageDetail.stop')}
                            value={packageDetail?.deliveryTaskOrder?.stopNumber != null ? formatStopNumber(packageDetail?.deliveryTaskOrder?.stopNumber,packageDetail?.deliveryTaskOrder?.subStopNumber) : '-'}
                        />
                        <InfoRow
                            label={t('pages.dsp.packageDetail.route')}
                            value={packageDetail?.deliveryTaskOrder?.routeName || '-'}
                        />
                        <InfoRow
                            label={t('pages.dsp.packageDetail.assignedTime')}
                            value={packageDetail?.deliverAt ? formatTimestampToSecondsTime(packageDetail?.deliverAt) : '-'}
                        />
                        <InfoRow
                            label={t('pages.dsp.packageDetail.deliveryAttempt')}
                            value={`${packageDetail?.deliveryAttempts || 0} out of 3`}
                        />
                    <OrderNoteItem 
                        orderNote={packageDetail?.deliveryTaskOrder?.orderNote}
                        currentLanguage={i18n.language}
                    />
                        <div className="flex justify-between py-2 border-gray-100">
                            <span
                                className="text-gray-600 text-sm">{`${t('pages.delivery.exception.filedReason')}`}:</span>
                            <span
                                className="text-sm font-medium text-right text-violet-600">
                                {exceptionSubReasons.length > 0
                                    ? exceptionSubReasons.map(s => getLocalizedText({ zh: s.textZh, en: s.textEn, es: s.textEs }, s.textEn)).join(', ')
                                    : getExceptionTypeText(exceptionReason)}
                            </span>
                        </div>
                    </div>
                    <h2 className="text-base font-medium">{t('pages.dsp.packageDetail.customerInfo')}</h2>
                    <div>
                        <InfoRow
                            label={t('pages.dsp.packageDetail.customerName')}
                            value={packageDetail?.recipientName || '-'}
                        />
                        <InfoRow
                            label={t('pages.dsp.packageDetail.phoneNumber')}
                            value={maskPhoneNumber(packageDetail?.recipientPhone)}
                        />
                        <InfoRow
                            label={t('pages.dsp.packageDetail.address')}
                            value={packageDetail?.recipientAddress || '-'}
                        />
                    </div>
                </div>

                {exceptionReason.isUploadsImages && (
                    <>
                        <div className="mb-2 bg-white p-4 border-t-1 shadow-sm">
                            <h2 className="text-base font-semibold mb-1">{t('pages.delivery.detail.photos')}</h2>
                            <p className="text-sm text-gray-600 mb-1">{`${t('pages.delivery.exception.photoDesc')}`}</p>
                            <div className="grid grid-cols-3 gap-4">
                                <PhotoUploadCard
                                    photoUrl={photos.label?.url}
                                    type="label"
                                    description=""
                                    onTakePhoto={takePhoto}
                                    onDelete={deletePhoto}
                                    onView={handleViewPhoto}
                                />
                                <PhotoUploadCard
                                    photoUrl={photos.location?.url}
                                    type="location"
                                    description=""
                                    onTakePhoto={takePhoto}
                                    onDelete={deletePhoto}
                                    onView={handleViewPhoto}
                                />
                            </div>
                        </div>

                        {/* 查看大图 */}
                        <PhotoViewer
                            photoUrl={viewingPhoto}
                            onClose={() => setViewingPhoto(null)}
                        />
                    </>
                )}
            </div>

            <div {...getBottomNavBarContainerStyle('flex-0 flex px-4 py-2 mt-auto gap-4')}>
                <Button
                    block
                    color="primary"
                    loading={isLoading}
                    disabled={isLoading || (exceptionReason.isUploadsImages && (!photos.label?.url || !photos.location?.url))}
                    onClick={handleCompleteException}
                >
                    {t('pages.delivery.detail.complete')}
                </Button>
            </div>
        </div>
    );
};

export default DspMarkExceptionPage;
